import { useCallback, useEffect, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { activeCallIdAtom, callsAtom } from '../states';
import {
  Call,
  CallDirection,
  CallStatus,
  CallUpdatesTypes,
  CallWithTimestamp,
  MakeOutgoingCallParams,
  UpdateOutgoingCallIdParams,
  UseCallOperationsProps,
} from '../typings';
import { CALL_CLEANUP_DELAY, CALL_EXPIRY_DURATION_MS } from '../constants';
import useWabaLevelPermission from '../../../shared/hooks/use-waba-level-permission';
import { Permissions } from '../../../shared/typings/user';
import useNotifications from '../../../shared/hooks/use-notifications';

export const useCallOperations = ({ onCallPicked, onCallCreated }: UseCallOperationsProps = {}) => {
  const [calls, setCalls] = useAtom(callsAtom);
  const [activeCallId, setActiveCallId] = useAtom(activeCallIdAtom);

  const { hasPermission } = useWabaLevelPermission();
  const { addNotification } = useNotifications();

  const pushNewCall = useCallback(
    (newCall: CallWithTimestamp, makeActive?: boolean) => {
      setCalls((prev) => {
        const existingCallIndex = prev.findIndex((call) => call.callId === newCall.callId);
        if (existingCallIndex !== -1) return prev;

        if (prev.length === 0 || makeActive) {
          setActiveCallId(newCall.callId);
        }
        return [...prev, newCall];
      });
      onCallCreated?.(newCall.callId);
    },
    [onCallCreated]
  );

  const removeCallById = useCallback(
    (callId: string) => {
      setCalls((prev) => prev.filter((call) => call.callId !== callId));
      setActiveCallId((prev) => (prev === callId ? null : prev));
    },
    [setCalls]
  );

  const updateOutgoingCallId = useCallback(
    ({ tempCallId, newCallId }: UpdateOutgoingCallIdParams) => {
      setCalls((prev) =>
        prev.map((c) => (c.callId === tempCallId ? { ...c, callId: newCallId, tempCallId: tempCallId } : c))
      );
      setActiveCallId((prev) => (prev === tempCallId ? newCallId : prev));
      onCallCreated?.(newCallId);
      return newCallId;
    },
    [setCalls, onCallCreated]
  );

  const makeOutgoingCall = useCallback(
    ({ customerName, customerNumber, wabaNumber, wabaName, integrationId, customerId }: MakeOutgoingCallParams) => {
      const hasInitiateCallPermission = hasPermission(integrationId, Permissions.INITIATE_CALL).hasAccess;
      if (!hasInitiateCallPermission) {
        addNotification({ type: 'error', message: 'You do not have permission to initiate calls' });
        return;
      }
      const tempCallId = `temp-${customerNumber}-${Date.now()}`;
      const newOutgoingCall: CallWithTimestamp = {
        customerName,
        customerNumber,
        customerId,
        wabaNumber,
        wabaName,
        integrationId,
        status: CallStatus.INITIATED,
        direction: CallDirection.OUTGOING,
        callId: tempCallId,
        sdp: '',
        sdpType: '',
        dateCreated: new Date().toISOString(),
        expiryTimestamp: Date.now() + CALL_EXPIRY_DURATION_MS,
        shouldConnect: true,
      };
      pushNewCall(newOutgoingCall, true);
      return tempCallId;
    },
    [pushNewCall]
  );

  // Cleanup disconnected calls after delay
  const cleanupCall = useCallback((callId: string) => {
    setTimeout(() => {
      removeCallById(callId);
    }, CALL_CLEANUP_DELAY);
  }, []);

  return {
    calls,
    setCalls,
    activeCallId,
    cleanupCall,
    setActiveCallId,
    pushNewCall,
    removeCallById,
    makeOutgoingCall,
    updateOutgoingCallId,
  };
};
